import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getComicsByCategory, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/article/${comic.id}`}
      className="group block cursor-pointer"
      data-product-shop=""
    >
      <div className="relative overflow-hidden rounded-lg aspect-[5/7] mb-3">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - ${comic.category}韩国漫画`}
          title={`${comic.title} - 桃桃漫画网免费在线`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-block bg-accent-500 text-foreground-950 text-xs px-2.5 py-1 rounded-full font-medium">
            {comic.status}
          </span>
        </div>
      </div>
      <span className="inline-block bg-accent-100 text-accent-800 text-xs px-2 py-0.5 rounded-full mb-2">
        {comic.category}
      </span>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
        {comic.title}
      </h3>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
        <span>{comic.author}</span>
        <span>{comic.chapters}话</span>
      </div>
    </Link>
  );
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug);
  const comics = useMemo(() => {
    const list = slug ? getComicsByCategory(slug) : [];
    const sorted = [...list];
    if (sortBy === 'popular') {
      sorted.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      sorted.sort((a, b) => b.rating - a.rating);
    } else {
      sorted.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }
    return sorted;
  }, [slug, sortBy]);

  const categoryName = currentCategory?.name || '全部漫画';
  const pageTitle = `${categoryName}韩国漫画 - 桃桃漫画网`;
  const pageDesc = `桃桃漫画网${categoryName}分类，为您精选${currentCategory?.count || '海量'}部优质韩国漫画，支持在线免费阅读。每日更新，精彩不断。`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
      },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: typeof window !== 'undefined' ? window.location.href : '',
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`韩国漫画,${categoryName},画免费,画漫画大全,桃桃漫画网,在线阅读`}
        canonicalUrl={`/category/${slug}`}
        schemaData={[breadcrumbSchema, collectionSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: categoryName }]} />

          {/* CTA Section */}
          <section className="flex flex-col lg:flex-row">
            <div className="lg:w-1/2 bg-background-100 px-4 md:px-8 py-16 md:py-24 flex items-center">
              <div className="max-w-md">
                <span className="text-xs font-medium text-foreground-500 uppercase tracking-wider">
                  {categoryName}
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mt-2 leading-tight font-heading">
                  {categoryName}
                  <br />
                  精选漫画
                </h1>
              </div>
            </div>
            <div className="lg:w-1/2 bg-background-50 px-4 md:px-8 py-16 md:py-24 flex items-center justify-center">
              <div className="max-w-md text-center">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  探索{categoryName}的世界
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  桃桃漫画网为您精选{categoryName}分类漫画，涵盖最热门、最新、最高评分的作品，随时随地免费阅读。
                </p>
                <div className="flex items-center justify-center gap-2">
                  {(['latest', 'popular', 'rating'] as const).map((key) => (
                    <button
                      key={key}
                      onClick={() => setSortBy(key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === key
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                      }`}
                    >
                      {key === 'latest' ? '最新' : key === 'popular' ? '最热' : '评分'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/70 sticky top-[72px] z-30">
            <div className="max-w-[1280px] mx-auto overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max px-1 py-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-secondary-100 hover:text-foreground-800'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Comic Grid */}
          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-lg font-bold text-foreground-400 uppercase tracking-wider">
                    {categoryName}
                  </h2>
                  <p className="text-xl md:text-2xl font-bold text-foreground-900 mt-1 font-heading">
                    全部作品
                  </p>
                </div>
                <span className="text-sm text-foreground-500">{comics.length} 部作品</span>
              </div>

              {comics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {comics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}